import { createContext, PropsWithChildren, useContext, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import DashboardTabs from './DashboardTabs';

const TabsHostContext = createContext<HTMLDivElement | null>(null);

// The host remains in the document when page headers are replaced, so an
// in-progress CSS transition can continue across route changes.
export const DashboardTabsProvider = ({ children }: PropsWithChildren) => {
  const [host] = useState(() => {
    const element = document.createElement('div');
    Object.assign(element.style, {
      position: 'fixed',
      width: '0',
      height: '0',
      zIndex: '3',
      visibility: 'hidden',
    });
    return element;
  });

  useLayoutEffect(() => {
    document.body.appendChild(host);
    return () => { host.remove(); };
  }, [host]);

  return (
    <TabsHostContext.Provider value={host}>
      {children}
      {createPortal(<DashboardTabs />, host)}
    </TabsHostContext.Provider>
  );
};

export const DashboardTabsSlot = () => {
  const host = useContext(TabsHostContext);
  const slot = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!host || !slot.current) return;
    const element = slot.current;
    const header = element.closest('header');
    let animationFrame: number | undefined;
    const position = () => {
      const rect = element.getBoundingClientRect();
      host.style.left = `${rect.left}px`;
      host.style.top = `${rect.top - 22}px`;
      host.style.visibility = 'visible';
    };
    const followTransition = (event: TransitionEvent) => {
      if (event.target !== header || event.propertyName !== 'transform') return;
      if (animationFrame !== undefined) cancelAnimationFrame(animationFrame);
      const follow = () => {
        position();
        animationFrame = requestAnimationFrame(follow);
      };
      follow();
    };
    const stopFollowing = (event: TransitionEvent) => {
      if (event.target !== header || event.propertyName !== 'transform') return;
      if (animationFrame !== undefined) cancelAnimationFrame(animationFrame);
      animationFrame = undefined;
      position();
    };
    position();
    header?.addEventListener('transitionrun', followTransition);
    header?.addEventListener('transitionend', stopFollowing);
    header?.addEventListener('transitioncancel', stopFollowing);
    window.addEventListener('resize', position);
    window.addEventListener('scroll', position, true);
    return () => {
      window.removeEventListener('resize', position);
      window.removeEventListener('scroll', position, true);
      header?.removeEventListener('transitionrun', followTransition);
      header?.removeEventListener('transitionend', stopFollowing);
      header?.removeEventListener('transitioncancel', stopFollowing);
      if (animationFrame !== undefined) cancelAnimationFrame(animationFrame);
      // A replacement header can claim the host in the same render.
      requestAnimationFrame(() => {
        if (!document.querySelector('[data-dashboard-tabs-slot]')) host.style.visibility = 'hidden';
      });
    };
  }, [host]);

  return <div ref={slot} data-dashboard-tabs-slot style={{ position: 'absolute', left: '50%', top: '50%' }} />;
};
